import { useEffect, useState } from 'react'
import DataTable from '../../components/DataTable/DataTable'
import { Button, Card, Loading, Tag, Tooltip, } from '@sede-enterprise/shell-ds-react-framework';
import { Filter } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components'
import { DocumentTitle } from '../../components/DocumentTitle/DocumentTitle';


import useGetCancuiList from './hooks/useGetCancuiList';
import { resultSentimentMap } from '../../util/constants';
import { consequenceProbSortingFn, criticalitySortingFn, sortListInOrder, getSummaryFromData } from './utils/cancuiUtils';
import PieChart from '../../components/PieChart/PieChart';
import { clickFilterHandler, getInitialData, updateColorsForUnmatchedLabels } from './utils/cancuiPieChartUtils';
import FilterModal from '../../components/FilterModal/FilterModal';


const INIT_PAGE = 1;
const INIT_ORDER = "equipment.id";
const INIT_ORDER_DIRECTION = 1
const equipmentStatsColumns = [{"label":"Units", "accessor": "unitCount"}, {"label":"Equipment", "accessor": "equipmentCount"}, {"label":"Zones", "accessor": "zoneCount"}];
const condensateRiskStatsColumns = [{"label":"Condensate Risk Detected", "accessor": "condensateRiskDetectedCount"}, {"label":"Condensate Risk Accepted", "accessor": "condensateRiskAcceptedCount"}, {"label":"Coating Condition Acceptable", "accessor": "coatingConditionAcceptableCount"}];
const Cancui = () => {

    const [currentPage, setCurrentPage] = useState(+sessionStorage.getItem("cancuiPage") || INIT_PAGE);
    const [cancuiOrder, setCancuiOrder] = useState(sessionStorage.getItem("cancuiOrder") || INIT_ORDER);
    const [orderDirection, setOrderDirection] = useState(+sessionStorage.getItem("cancuiorderDirection") || INIT_ORDER_DIRECTION);
    const [susceptibilityStatusDataToPlot, setSusceptibilityStatusDataToPlot] = useState(null);
    const [staticDynamicDataToPlot, setStaticDynamicDataToPlot] = useState(null);
    const [condensateRiskDataToPlot, setCondensateRiskDataToPlot] = useState(null);
    const [filterType, setFilterType] = useState(null)
    const [susceptibilityStatusSummary, setSusceptibilityStatusSummary] = useState(null)
    const [staticDynamicSummary, setStaticDynamicSummary] = useState(null)
    const [countSummary, setCountSummary] = useState(null)
    const [condensateRiskStatsSummary, setCondensateRiskStatsSummary] = useState(null)
    const [condensateRiskAssessmentSummary, setCondensateRiskAssessmentSummary] = useState(null)
    const [filteredData, setFilteredData] = useState(null);
    const [open, setOpen] = useState(false);
    const handleFilterModalClose = () => {
        setOpen(false);
    }

    // Settting the filter for the sensor data
    const [sensorFilter, setSensorFilter] = useState(sessionStorage.getItem("cancuiApplyfilter") || ``);


    // Handling the sensor filter change
    const handleSensorFilterChange = (appliedFilter, filterFormValues) => {
        if (appliedFilter) {
            sessionStorage.setItem('cancuiApplyfilter', appliedFilter);
            sessionStorage.setItem('cancuiFilterValues', JSON.stringify(filterFormValues));
        } else {
            sessionStorage.removeItem('cancuiApplyfilter');
            sessionStorage.removeItem('cancuiFilterValues');
        }
        setSensorFilter(appliedFilter);
    }

    // //Clearing the sensor filter
    const clearSensorFilter = () => {
        sessionStorage.removeItem('cancuiApplyfilter');
        sessionStorage.removeItem('cancuiFilterValues');
        setSensorFilter(``);
    }


    const handleOrderChange = (sortBy, direction) => {
        setCancuiOrder(sortBy);
        sessionStorage.setItem('cancuiOrder', sortBy);

        setOrderDirection(+direction);
        sessionStorage.setItem('cancuiDirection', +direction);
    }

    const handleCurrentPageChange = (current) => {
        sessionStorage.setItem('cancuiPage', current)
        setCurrentPage(current);

    }

    const { data: outputData, isLoading } = useGetCancuiList(sensorFilter);


    const columns = [
        {
            header: 'Unit',
            accessorFn: (row) => row?.unit.id,
            id: 'unit.id',
            cell: (props) => {
                const row = props.row.original;
                return (row?.unit?.name ? row.unit.name: row?.unit?.id)
            }
        },

        {
            header: 'Equipment',
            accessorFn: (row) => row?.equipment.id,
            id: 'equipment.id',
            cell: (props) => {
                const row = props.row.original;
                return (row?.equipment?.name ? row.equipment.name: row?.equipment?.id)
            }
        },
        {
            header: 'Zones',
            accessorFn: (row) => row?.zones,
            id: 'zones',
            sortingFn: sortListInOrder,
            cell: (props) => {
                const row = props.row.original;
                return (row?.zones?.map((value, index) => (
                    <>
                    <div key={index} className={row.contributedIndex === index && row?.zones?.length > 1 ? 'cancui-cell-container bold-text' : 'cancui-cell-container'}>{value}</div>
                    {index < row?.zones?.length - 1 && <hr className="hr_split"/>}
                    </>
                )))
            },

        },
        {
            header: 'Probability Flag',
            accessorFn: (row) => row?.probabilityFlag,
            id: 'probabilityFlag',
            sortingFn: consequenceProbSortingFn,
            cell: (props) => {
                const row = props.row.original;
                return (row?.probabilityFlag?.map((value, index) => (
                    <>
                    <div key={index} className={row.contributedIndex === index && row?.zones?.length > 1 ? 'cancui-cell-container bold-text' : 'cancui-cell-container'}>{value}</div>
                    {index < row?.probabilityFlag?.length - 1 && <hr className="hr_split"/>}
                    </>
                )))
            },

        },
        {
            header: 'Consequence',
            accessorFn: (row) => row?.consequence,
            id: 'consequence',
            sortingFn: consequenceProbSortingFn,
            cell: (props) => {
                const row = props.row.original;
                return (row?.consequence?.map((value, index) => (
                    <>
                    <div key={index} className={row.contributedIndex === index && row?.zones?.length > 1 ? 'cancui-cell-container bold-text' : 'cancui-cell-container'}>{value}</div>
                    {index < row?.consequence?.length - 1 && <hr className="hr_split"/>}
                    </>
                )))
            }

        },
        {
            header: 'Criticality',
            accessorFn: (row) => row?.criticality,
            id: 'criticality',
            sortingFn: criticalitySortingFn,
            cell: (props) => {
                const row = props.row.original;
                return (row?.criticality?.map((value, index) => (
                    <>
                    <div key={index} className={row.contributedIndex === index && row?.zones?.length > 1 ? 'cancui-cell-container bold-text' : 'cancui-cell-container'}>{value}</div>
                    {index < row?.criticality?.length - 1 && <hr className="hr_split"/>}
                    </>
                )))
            }
        },
        {
            header: 'Temperature Method',
            accessorFn: (row) => row?.temperatureMethod,
            sortingFn: sortListInOrder,
            id: 'temperatureMethod',
            cell: (props) => {
                const row = props.row.original;
                return (row?.temperatureMethod?.map((value, index) => (
                    <>
                    <div key={index} className={row.contributedIndex === index && row?.zones?.length > 1 ? 'cancui-cell-container bold-text' : 'cancui-cell-container'}>{value}</div>
                    {index < row?.temperatureMethod?.length - 1 && <hr className="hr_split"/>}
                    </>
                )))
            }
        },
        {
            header: 'Condensate Risk Hours',
            accessorFn: (row) => row?.condensateRiskHours,
            sortingFn: sortListInOrder,
            id: 'condensateRiskHours',
            cell: (props) => {
                const row = props.row.original;
                return (row?.condensateRiskHours?.map((value, index) => (
                    <>
                    <div key={index} className={row.contributedIndex === index && row?.zones?.length > 1 ? 'cancui-cell-container bold-text' : 'cancui-cell-container'}>{value}</div>
                    {index < row?.condensateRiskHours?.length - 1 && <hr className="hr_split"/>}
                    </>
                )))
            }
        },
        {
            header: 'Susceptibility Status',
            accessorFn: (row) => row?.susceptibilityStatus,
            sortingFn: sortListInOrder,
            cell: (props) => {
                const row = props.row.original;
                console.log("Rendering Susceptibility Status for row:", row)
                return (
                    row?.susceptibilityStatus?.map((value, index) => {
                        index = parseInt(index);
                        console.log("row?.needsUserAttention", row?.needsUserAttention, index, row?.needsUserAttention[index])
                        
                        return (
                        <>
                        {row?.needsUserAttention?.[index] ? 
                            <Tooltip className='tooltip' placement='bottom' 
                            overlay={<div className='tooltip-content'>{"High Consequence equipment. Temperature is currently measured using a static method. User review required. Sensor installation is recommended for closer oversight."}</div>} 
                            trigger={
                                <div key={index} className={'cancui-cell-container'}>
                                <Tag
                                    size='small'
                                    sentiment={resultSentimentMap[value]}
                                    variant='outlined'
                                    shape='round'
                                >
                                    <span className={row.contributedIndex === index && row?.zones?.length > 1 ? 'bold-text' : ''}>{value}</span>
                                </Tag>
                                <div>
                                <span style={{color: 'rgba(165, 2, 2, 1)', fontSize: '30px', marginLeft: '4px', fontWeight: 'bold'}}>!</span>                            
                                </div>
                                </div>} /> : 
                                <div key={index} className={'cancui-cell-container'}>
                                    <Tag
                                        size='small'
                                        sentiment={resultSentimentMap[value]}
                                        variant='outlined'
                                        shape='round'
                                    >
                                        <span className={row.contributedIndex === index && row?.zones?.length > 1 ? 'bold-text' : ''}>{value}</span>
                                    </Tag>
                                </div>}
                        {index < row?.susceptibilityStatus?.length - 1 && <hr className="hr_split"/>}
                        </>
                        )
                }
                    )
                )
            },
            id: 'susceptibilityStatus',
        }

    ];


    const applyPieFilters = (type, targetLabel) => {
        if (type === "susceptibilityStatus") {
            const filtered = outputData.filter(item => item.susceptibilityStatus?.some(status => status === targetLabel));
            console.log("Filtered Data for Susceptibility Status:", filtered);
            setFilteredData(filtered);
        }
        if (type === "staticDynamic") {
            const filtered = outputData.filter(item => item.temperatureMethod?.some(status => status === targetLabel));
            setFilteredData(filtered);
        }
        if (type === "condensateRisk") {
            const filtered = outputData.filter(item => item.condensateRiskAssementStatus?.some(status => status === targetLabel));
            setFilteredData(filtered);
        }
    }

    useEffect(() => {
            setFilteredData(outputData)
            const { susceptibilityStatusSummary, staticDynamicSummary, condensateRiskAssessmentSummary, countSummary, condensateRiskStatsSummary } = getSummaryFromData(outputData);
            console.log("Summary:", susceptibilityStatusSummary, staticDynamicSummary, condensateRiskAssessmentSummary, countSummary, condensateRiskStatsSummary);
            setSusceptibilityStatusSummary(susceptibilityStatusSummary);
            setStaticDynamicSummary(staticDynamicSummary);
            setCondensateRiskAssessmentSummary(condensateRiskAssessmentSummary);
            setCountSummary(countSummary);
            setCondensateRiskStatsSummary(condensateRiskStatsSummary);
            getInitialData(susceptibilityStatusSummary, staticDynamicSummary, condensateRiskAssessmentSummary, setSusceptibilityStatusDataToPlot, setStaticDynamicDataToPlot, setCondensateRiskDataToPlot);
        }, [outputData]);


    return (
        <>
            <DocumentTitle title="CAN-CUI" />
            <div className='parent-container'>
                <div className='content-container'>
                    <div className='content-header cam-header'>
                        <h3 className='cancui-header-title'>CAN-CUI</h3>
                        <Tooltip id='home-id-5' overlay={<span id='home-id-6'>Filter Options</span>}
                            placement='bottom'
                            trigger={<Button data-testid="filter-btn" id='home-id-7' iconOnly icon={<Filter id='home-id-8' />}
                                variant='filled' onClick={() => setOpen(true)} />} />
                    </div>
                    <div className='content-body'>
                        <div className='model-content-body-header'>
                            <h4>Summary</h4>
                        </div>
                        <Card bodyPadding={isLoading}>
                            {isLoading && <div className='loading-container'> <Loading /> </div>}
                            {!isLoading && <>
                            {sensorFilter && <h4 style={{marginLeft: '30px', marginBottom: 0, color: '#dda71dff'}}>Note: Stats are based on the filter selected</h4>}
                            <div className='summary-cards-container'>
                                <div className='summary-card-tables'>
                                    <h4>Onboarded Equipment Stats</h4>
                                    {countSummary && 
                                        <table className='unit-summary-table'>
                                            <thead>
                                                <tr>
                                                    <th>Hierarchy</th>
                                                    <th>Count</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {equipmentStatsColumns.map(item => (
                                                    <tr key={item.label}>
                                                        <td>{item.label}</td>
                                                        <td>{countSummary[item.accessor]}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    }
                                </div>
                                <div className='summary-card-tables'>
                                    <h4>Condensate Risk Stats</h4>
                                    {condensateRiskStatsSummary && 
                                        <table className='unit-summary-table'>
                                            <thead>
                                                <tr>
                                                    <th>Condensate Risk Status</th>
                                                    <th>Equipment Zone Count</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {condensateRiskStatsColumns.map(item => (
                                                    <tr key={item.label}>
                                                        <td>{item.label}</td>
                                                        <td>{condensateRiskStatsSummary[item.accessor]}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    }
                                </div>
                            </div>
                            <div className='summary-cards-container'>
                                <div className='summary-card'>
                                    {susceptibilityStatusDataToPlot && <PieChart
                                        onPieClick={(pieData) => clickFilterHandler(pieData, 
                                            susceptibilityStatusDataToPlot, 
                                            staticDynamicDataToPlot, 
                                            condensateRiskDataToPlot, 
                                            setSusceptibilityStatusDataToPlot, 
                                            setStaticDynamicDataToPlot, 
                                            setCondensateRiskDataToPlot, 
                                            (dataToPlot, targetLabel, type) =>
                                                updateColorsForUnmatchedLabels(dataToPlot, targetLabel, type, setFilterType, applyPieFilters))}
                                        clearPieFilters={() => {
                                            setFilterType(null)
                                            setFilteredData(outputData)
                                            getInitialData(susceptibilityStatusSummary, staticDynamicSummary, condensateRiskAssessmentSummary, setSusceptibilityStatusDataToPlot, setStaticDynamicDataToPlot, setCondensateRiskDataToPlot);
                                        }}
                                        data={susceptibilityStatusDataToPlot}
                                        selectedPieFilterType={filterType}
                                        pieFilterType={'susceptibilityStatus'}
                                        title={'Susceptibility Status'}
                                        helpText={"This depicts the number of equipment under each susceptibility status category. Click on the Pie to filter the equipment list based on susceptibility status."}
                                    />}
                                </div>
                                <div className='summary-card'>
                                    {staticDynamicDataToPlot && <PieChart
                                        onPieClick={(pieData) => clickFilterHandler(pieData, 
                                            susceptibilityStatusDataToPlot, 
                                            staticDynamicDataToPlot, 
                                            condensateRiskDataToPlot, 
                                            setSusceptibilityStatusDataToPlot, 
                                            setStaticDynamicDataToPlot, 
                                            setCondensateRiskDataToPlot, 
                                            (dataToPlot, targetLabel, type) =>
                                                updateColorsForUnmatchedLabels(dataToPlot, targetLabel, type, setFilterType, applyPieFilters))}
                                        clearPieFilters={() => {
                                            setFilterType(null)
                                            setFilteredData(outputData)
                                            getInitialData(susceptibilityStatusSummary, staticDynamicSummary, condensateRiskAssessmentSummary, setSusceptibilityStatusDataToPlot, setStaticDynamicDataToPlot, setCondensateRiskDataToPlot);
                                        }}
                                        data={staticDynamicDataToPlot}
                                        selectedPieFilterType={filterType}
                                        pieFilterType={'staticDynamic'}
                                        title={'Static vs Real-Time Temperature'}
                                        helpText={"This depicts the number of equipment under each static/real-time temperature method. Click on the Pie to filter the equipment list based on static/real-time temperature method."}
                                    />}
                                </div>
                                <div className='summary-card'>
                                    {condensateRiskDataToPlot && <PieChart
                                        onPieClick={(pieData) => clickFilterHandler(pieData, 
                                            susceptibilityStatusDataToPlot, 
                                            staticDynamicDataToPlot, 
                                            condensateRiskDataToPlot, 
                                            setSusceptibilityStatusDataToPlot, 
                                            setStaticDynamicDataToPlot, 
                                            setCondensateRiskDataToPlot, 
                                            (dataToPlot, targetLabel, type) =>
                                                updateColorsForUnmatchedLabels(dataToPlot, targetLabel, type, setFilterType, applyPieFilters))}
                                        clearPieFilters={() => {
                                            setFilterType(null)
                                            setFilteredData(outputData)
                                            getInitialData(susceptibilityStatusSummary, staticDynamicSummary, condensateRiskAssessmentSummary, setSusceptibilityStatusDataToPlot, setStaticDynamicDataToPlot, setCondensateRiskDataToPlot);
                                        }}
                                        data={condensateRiskDataToPlot}
                                        selectedPieFilterType={filterType}
                                        pieFilterType={'condensateRisk'}
                                        title={'Condensate Risk Assessment Status'}
                                        helpText={"This depicts the number of equipment for which condensate risk assessment is pending/resolved. Click on the Pie to filter the equipment list based on condensate risk assessment completion."}
                                    />}
                                </div>
                            </div>
                            </>}
                        </Card>
                        <div className='model-content-body-header'>
                            <h4>Equipment List</h4>
                        </div>
                        {isLoading && <div className='loading-container'> <Loading /> </div>}
                        {!isLoading && filteredData.length > 0 && <DataTable
                            data={filteredData}
                            columns={columns}
                            count={filteredData.length}
                            currentPage={+sessionStorage.getItem("cancuiPage") || INIT_PAGE}
                            handleCurrentPageChange={handleCurrentPageChange}
                            handleOrderChange={handleOrderChange}
                            INIT_ORDER={sessionStorage.getItem("cancuiOrder") || INIT_ORDER}
                            INIT_ORDER_DIRECTION={+sessionStorage.getItem("cancuiorderDirection") || INIT_ORDER_DIRECTION}
                            loading={isLoading}
                            paginationMode="client"
                            isCancui={true}
                        />}

                    </div>
                </div>
            </div>
            <FilterModal open={open} onClose={handleFilterModalClose}
                handleFilterChange={handleSensorFilterChange}
                handleFilterClear={clearSensorFilter}
                filterKey="cancuiFilterValues"
                id='home-id-22'
                isCancui={true}
            />

        </>
    )
}

export default Cancui